/**
 * Title Tag Transformer
 * Transforms WooRank title criteria data into React component-ready format
 */

import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
  TITLE_THRESHOLDS,
} from '../../constants/seoMappings';

/**
 * Generates a scenario-based message for the title tag
 * Validates by both character count and pixel width
 * @param {Object} data - The title criteria data
 * @param {string} url - The URL being analyzed
 * @returns {string} - The appropriate message based on the scenario
 */
const generateMessage = (data, url = '') => {
  // Missing title scenario
  if (!data?.value) {
    return `The title tag for ${url || 'this page'} is missing.`;
  }

  const length = data?.length || 0;
  const pixelLength = data?.pixelLength || 0;

  // Check character length issues first
  if (length > TITLE_THRESHOLDS.MAX_LENGTH) {
    return `Title is too long (${length} characters) – limit to ${TITLE_THRESHOLDS.MAX_LENGTH} characters.`;
  }

  if (length < TITLE_THRESHOLDS.MIN_LENGTH) {
    return `Title is too short (${length} characters) – aim for ${TITLE_THRESHOLDS.MIN_LENGTH}-${TITLE_THRESHOLDS.MAX_LENGTH} characters.`;
  }

  // Check pixel width issues
  if (pixelLength > TITLE_THRESHOLDS.MAX_PIXELS) {
    return `Title pixel width is too wide (${pixelLength}px) – limit to ${TITLE_THRESHOLDS.MAX_PIXELS}px.`;
  }

  if (pixelLength < TITLE_THRESHOLDS.MIN_PIXELS) {
    return `Title pixel width is too narrow (${pixelLength}px) – aim for ${TITLE_THRESHOLDS.MIN_PIXELS}-${TITLE_THRESHOLDS.MAX_PIXELS}px.`;
  }

  // Good length scenario
  return `Title length is good (${length} characters, ${pixelLength}px).`;
};

/**
 * Transforms the title criteria from WooRank API response
 * @param {Object} titleCriteria - The title criteria object from the API
 * @param {string} url - The URL being analyzed
 * @returns {Object} - Transformed title tag data for React components
 */
export const transformTitleTag = (titleCriteria, url = '') => {
  // Handle missing criteria entirely
  if (!titleCriteria) {
    return {
      status: 'error',
      iconType: 'error',
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: `The title tag for ${url || 'this page'} is missing.`,
      data: null,
    };
  }

  const data = titleCriteria?.data || null;
  const wooRankStatus = titleCriteria?.status || 'bad';
  const importance = titleCriteria?.importance || 0;
  const solvability = titleCriteria?.solvability || 0;

  // Check if data is empty/missing
  const isEmpty = !data?.value;

  return {
    status: getStatus(wooRankStatus),
    iconType: getIconType(wooRankStatus),
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    message: generateMessage(data, url),
    data: isEmpty ? null : {
      value: data?.value || '',
      length: data?.length || 0,
      pixelLength: data?.pixelLength || 0,
    },
  };
};

export default transformTitleTag;

